import React, { useContext, useState, useEffect } from "react";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    DialogActions,
    Button,
    Input,
    Field,
    Spinner,
    Checkbox,
    Tooltip,
} from "@fluentui/react-components";
import { Clock24Regular, Clock24Filled } from "@fluentui/react-icons";
import { ApiContext } from "../providers/ApiProvider";
import { ITimeEntry, IUser } from "../types/types";
import moment from "moment";

interface IQuickFillWeekDialogProps {
    token: string;
    user: IUser;
    projectId: string;
    projectName: string;
    selectedDays: number[];
    onSuccess: (updatedFieldNames?: string[]) => void;
}

export default function QuickFillWeekDialog(props: IQuickFillWeekDialogProps) {
    const { submitTimeEntry, updateTimeEntry, getTimeEntriesBetween } = useContext(ApiContext);
    const [isOpen, setIsOpen] = useState(false);
    const [hoursPerDay, setHoursPerDay] = useState<string>("7.5");
    const [includeWeekends, setIncludeWeekends] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<{ date: string; hours: number; dayName: string }[]>([]);

    // Calculate preview when inputs change
    useEffect(() => {
        if (!hoursPerDay || !props.selectedDays || props.selectedDays.length === 0) {
            setPreview([]);
            return;
        }

        try {
            const hoursPerDayNum = parseFloat(hoursPerDay);
            if (isNaN(hoursPerDayNum) || hoursPerDayNum <= 0 || hoursPerDayNum > 24) {
                setError("Hours per day must be between 0 and 24");
                setPreview([]);
                return;
            }

            setError("");

            // Get weekdays (and optionally weekends) from selectedDays
            // selectedDays[1-7] are Monday-Sunday of current week
            const previewData: { date: string; hours: number; dayName: string }[] = [];
            const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

            for (let i = 1; i <= 7; i++) {
                const dayTimestamp = props.selectedDays[i];
                const day = new Date(dayTimestamp);
                const dayOfWeek = day.getDay();

                // Include weekdays (Mon-Fri) always, weekends (Sat-Sun) if includeWeekends is true
                if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                    // Weekday
                    const year = day.getFullYear();
                    const month = String(day.getMonth() + 1).padStart(2, "0");
                    const date = String(day.getDate()).padStart(2, "0");
                    previewData.push({
                        date: `${year}-${month}-${date}`,
                        hours: hoursPerDayNum,
                        dayName: dayNames[dayOfWeek],
                    });
                } else if (includeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
                    // Weekend
                    const year = day.getFullYear();
                    const month = String(day.getMonth() + 1).padStart(2, "0");
                    const date = String(day.getDate()).padStart(2, "0");
                    previewData.push({
                        date: `${year}-${month}-${date}`,
                        hours: hoursPerDayNum,
                        dayName: dayNames[dayOfWeek],
                    });
                }
            }

            setPreview(previewData);
        } catch (err) {
            setError("Invalid input");
            setPreview([]);
        }
    }, [hoursPerDay, includeWeekends, props.selectedDays]);

    const handleSubmit = async () => {
        if (!hoursPerDay || preview.length === 0) {
            setError("Please fill in all required fields");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            // Get date range for fetching existing entries
            const weekStart = props.selectedDays[1];
            const weekEnd = props.selectedDays[8];
            const startTimestamp = moment(weekStart).utc(true).startOf("day").valueOf();
            const endTimestamp = moment(weekEnd).utc(true).endOf("day").valueOf();

            // Fetch existing time entries for the date range
            const existingEntries = await getTimeEntriesBetween(props.token, {
                from: startTimestamp,
                to: endTimestamp,
            });

            // Create a map of existing entries by project_id and date
            const existingEntriesMap = new Map<string, ITimeEntry>();
            if (existingEntries && existingEntries.timeEntries) {
                existingEntries.timeEntries.forEach((entry: ITimeEntry) => {
                    if (entry.project_id === props.projectId) {
                        // Format date in local timezone (YYYY-MM-DD)
                        const entryDate = new Date(entry.from);
                        const year = entryDate.getFullYear();
                        const month = String(entryDate.getMonth() + 1).padStart(2, "0");
                        const date = String(entryDate.getDate()).padStart(2, "0");
                        const dateKey = `${year}-${month}-${date}`;
                        existingEntriesMap.set(dateKey, entry);
                    }
                });
            }

            const today = new Date();
            const promises: Promise<void>[] = [];
            const updatedFieldNames: string[] = []; // Track which fields were updated

            // Process each day in preview
            for (const item of preview) {
                // Parse date using moment UTC (matching system convention)
                const fromTimestamp = moment(item.date).utc(true).startOf("day").valueOf();
                const toTimestamp = fromTimestamp + item.hours * 3600 * 1000;

                // Create field name in format: projectID_timestamp
                const fieldName = `${props.projectId}_${fromTimestamp}`;
                updatedFieldNames.push(fieldName);

                const existingEntry = existingEntriesMap.get(item.date);

                if (existingEntry) {
                    // Update existing entry
                    const updatedEntry: ITimeEntry = {
                        ...existingEntry,
                        from: fromTimestamp,
                        to: toTimestamp,
                        modified: today.valueOf(),
                    };
                    promises.push(updateTimeEntry(props.token, updatedEntry, existingEntry.id!));
                } else {
                    // Create new entry
                    const newEntry: ITimeEntry = {
                        project_id: props.projectId,
                        created_by: props.user.id,
                        created: today.valueOf(),
                        modified: today.valueOf(),
                        description: "",
                        from: fromTimestamp,
                        to: toTimestamp,
                        approved: false,
                    };
                    promises.push(submitTimeEntry(props.token, newEntry));
                }
            }

            await Promise.all(promises);

            // Clear local change tracking for updated fields so merge logic uses server values
            setIsOpen(false);
            props.onSuccess(updatedFieldNames);
        } catch (err: any) {
            console.error("Error filling hours:", err);
            setError(err.message || "Failed to fill hours. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen}>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content="Quick fill current week" relationship="label">
                    <Button
                        appearance="subtle"
                        size="small"
                        onClick={() => setIsOpen(true)}
                        icon={<Clock24Regular />}
                        className="opacity-70 hover:opacity-100 group-hover:opacity-100 transition-opacity shrink-0 min-w-0 w-8 h-8 p-0 bg-transparent! hover:bg-transparent! active:bg-transparent! focus:bg-transparent!"
                        style={{ backgroundColor: "transparent" }}
                        title="Quick fill current week"
                    />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface className="min-w-[90%] md:min-w-[500px] max-w-[600px]">
                <DialogBody>
                    <DialogTitle>Quick Fill Week - {props.projectName}</DialogTitle>
                    <DialogContent>
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-8 gap-4">
                                <Spinner size="large" />
                                <p>Filling hours...</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4 py-4">
                                {/* Hours Per Day */}
                                <Field label="Hours Per Day" required>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="24"
                                        value={hoursPerDay}
                                        onChange={(e) => setHoursPerDay(e.target.value)}
                                        placeholder="e.g., 7.5"
                                    />
                                </Field>

                                {/* Include Weekends */}
                                <Field>
                                    <Checkbox
                                        checked={includeWeekends}
                                        onChange={(_, data) => setIncludeWeekends(data.checked === true)}
                                        label="Include weekends (Saturday & Sunday)"
                                    />
                                </Field>

                                {/* Error Message */}
                                {error && (
                                    <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Preview */}
                                {preview.length > 0 && (
                                    <div className="mt-4">
                                        <Field label={`Preview (${preview.length} days will be filled)`}>
                                            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2">
                                                <div className="grid grid-cols-3 gap-2 text-sm">
                                                    <div className="font-semibold">Day</div>
                                                    <div className="font-semibold">Date</div>
                                                    <div className="font-semibold">Hours</div>
                                                    {preview.map((item, idx) => (
                                                        <React.Fragment key={idx}>
                                                            <div>{item.dayName}</div>
                                                            <div>{item.date}</div>
                                                            <div>{item.hours.toFixed(1)}</div>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        </Field>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" onClick={() => setIsOpen(false)} disabled={isLoading}>
                                Cancel
                            </Button>
                        </DialogTrigger>
                        <Button appearance="primary" onClick={handleSubmit} disabled={isLoading || preview.length === 0}>
                            Fill Hours
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
